import { getSupabaseClient, resilientFetch } from "@/lib/supabase/client";

export type ProgressEventType =
  | "diet_completed"
  | "diet_log_filled"
  | "water_goal_completed"
  | "meditation_completed"
  | "breath_completed"
  | "ritual_completed"
  | "task_completed"
  | "general_post"
  | "affirmation_post"
  | "meditation_share_post"
  | "ritual_share_post"
  | "energy_sent"
  | "healing_sent"
  | "daily_login";

type ProgressAuthHeaders = {
  "Content-Type": string;
  Authorization: string;
};

type PendingProgressEvent = {
  id: string;
  payload: {
    eventType: ProgressEventType;
    dateKey?: string;
    sourceId?: string;
    occurredAt?: string;
    metadata?: Record<string, unknown>;
  };
  attemptCount: number;
  nextAttemptAt: number;
  createdAt: number;
};

export type NotificationInboxItem = {
  id: string;
  type: string;
  title: string;
  body: string;
  data: Record<string, unknown>;
  deep_link: string | null;
  status: "unread" | "read";
  channel: "in_app" | "push" | "email";
  created_at: string;
  opened_at: string | null;
  clicked_at: string | null;
  actor_user_id: string | null;
  actor_display_name?: string | null;
};

export type NotificationPreferences = {
  user_id: string;
  allow_message: boolean;
  allow_energy: boolean;
  allow_match: boolean;
  allow_system: boolean;
  push_enabled: boolean;
  inbox_enabled: boolean;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
};

const PROGRESS_OUTBOX_KEY = "karma_progress_outbox_v1";
const PROGRESS_OUTBOX_MAX = 120;
let replayBound = false;
let replayTimer: ReturnType<typeof setTimeout> | null = null;

function readRuntimeEnv(name: string) {
  const value = (import.meta as any).env?.[name];
  if (typeof value === "string" && value.trim()) return value.trim();

  try {
    const w = typeof window !== "undefined" ? (window as any) : null;
    const injected = w?.__ENV__?.[name];
    if (typeof injected === "string" && injected.trim()) return injected.trim();
  } catch {
    // ignore
  }

  return "";
}

function makeOutboxId() {
  return `evt_${Math.random().toString(36).slice(2)}_${Date.now()}`;
}

function readOutbox(): PendingProgressEvent[] {
  try {
    const raw = globalThis.localStorage?.getItem(PROGRESS_OUTBOX_KEY) ?? "[]";
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => {
        const evt = item as Partial<PendingProgressEvent>;
        if (!evt || typeof evt !== "object") return null;
        if (!evt.payload || typeof evt.payload !== "object") return null;
        if (typeof evt.payload.eventType !== "string") return null;
        return {
          id: typeof evt.id === "string" && evt.id ? evt.id : makeOutboxId(),
          payload: evt.payload as PendingProgressEvent["payload"],
          attemptCount: Number.isFinite(Number(evt.attemptCount)) ? Math.max(0, Math.trunc(Number(evt.attemptCount))) : 0,
          nextAttemptAt: Number.isFinite(Number(evt.nextAttemptAt)) ? Math.max(0, Math.trunc(Number(evt.nextAttemptAt))) : Date.now(),
          createdAt: Number.isFinite(Number(evt.createdAt)) ? Math.max(0, Math.trunc(Number(evt.createdAt))) : Date.now(),
        };
      })
      .filter(Boolean) as PendingProgressEvent[];
  } catch {
    return [];
  }
}

function writeOutbox(items: PendingProgressEvent[]) {
  try {
    globalThis.localStorage?.setItem(PROGRESS_OUTBOX_KEY, JSON.stringify(items.slice(0, PROGRESS_OUTBOX_MAX)));
  } catch {
    // ignore
  }
}

function enqueueProgressEvent(payload: PendingProgressEvent["payload"], attemptCount = 0) {
  const now = Date.now();
  const delayMs = Math.min(60 * 60 * 1000, Math.pow(2, Math.max(0, attemptCount)) * 5_000);
  const next: PendingProgressEvent = {
    id: makeOutboxId(),
    payload,
    attemptCount,
    nextAttemptAt: now + delayMs,
    createdAt: now,
  };

  const current = readOutbox();
  current.unshift(next);
  writeOutbox(current);
  scheduleReplay();
}

function removeFromOutbox(id: string) {
  const current = readOutbox();
  writeOutbox(current.filter((item) => item.id !== id));
}

function upsertOutboxEvent(evt: PendingProgressEvent) {
  const current = readOutbox().filter((item) => item.id !== evt.id);
  current.unshift(evt);
  writeOutbox(current);
}

function isNetworkLikeError(error: unknown) {
  const msg = error instanceof Error ? error.message.toLowerCase() : "";
  return msg.includes("failed to fetch") || msg.includes("network") || msg.includes("abort") || msg.includes("timeout");
}

function scheduleReplay() {
  if (replayTimer) return;
  replayTimer = globalThis.setTimeout(() => {
    replayTimer = null;
    void flushProgressOutbox();
  }, 1500);
}

async function getProgressAuthHeaders(): Promise<ProgressAuthHeaders | null> {
  const supabase = getSupabaseClient();
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) return null;

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

export async function flushProgressOutbox() {
  const headers = await getProgressAuthHeaders();
  if (!headers) return;

  const now = Date.now();
  const queued = readOutbox();
  const due = queued.filter((item) => item.nextAttemptAt <= now).slice(0, 20);

  for (const item of due) {
    try {
      const response = await resilientFetch("/api/numeroloji/progress/events", {
        method: "POST",
        headers,
        body: JSON.stringify({
          eventType: item.payload.eventType,
          dateKey: item.payload.dateKey,
          sourceId: item.payload.sourceId,
          occurredAt: item.payload.occurredAt,
          metadata: item.payload.metadata ?? {},
        }),
      });

      if (!response.ok) {
        const json = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(json?.error || "Progress event kaydedilemedi");
      }

      removeFromOutbox(item.id);
    } catch (error) {
      if (!isNetworkLikeError(error)) {
        removeFromOutbox(item.id);
        continue;
      }

      const nextAttemptCount = item.attemptCount + 1;
      upsertOutboxEvent({
        ...item,
        attemptCount: nextAttemptCount,
        nextAttemptAt: Date.now() + Math.min(60 * 60 * 1000, Math.pow(2, nextAttemptCount) * 5_000),
      });
    }
  }

  const stillQueued = readOutbox();
  if (stillQueued.some((item) => item.nextAttemptAt <= Date.now() + 2_000)) {
    scheduleReplay();
  }
}

function ensureReplayListeners() {
  if (replayBound || typeof window === "undefined") return;
  replayBound = true;
  window.addEventListener("online", () => {
    void flushProgressOutbox();
  });
  window.addEventListener("focus", () => {
    void flushProgressOutbox();
  });
  scheduleReplay();
}

export async function sendProgressEvent(args: {
  eventType: ProgressEventType;
  dateKey?: string;
  sourceId?: string;
  occurredAt?: string;
  metadata?: Record<string, unknown>;
}) {
  ensureReplayListeners();

  const headers = await getProgressAuthHeaders();
  if (!headers) return null;

  const payload = {
    eventType: args.eventType,
    dateKey: args.dateKey,
    sourceId: args.sourceId,
    occurredAt: args.occurredAt,
    metadata: args.metadata ?? {},
  };

  try {
    const response = await resilientFetch("/api/numeroloji/progress/events", {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    const json = (await response.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
    if (!response.ok) {
      throw new Error(json?.error || "Progress event kaydedilemedi");
    }

    return json;
  } catch (error) {
    if (isNetworkLikeError(error)) {
      enqueueProgressEvent(payload);
      return { ok: true, queued: true };
    }
    throw error;
  }
}

export async function fetchProgressSummary() {
  const headers = await getProgressAuthHeaders();
  if (!headers) return null;

  try {
    const response = await resilientFetch("/api/numeroloji/progress/summary", {
      method: "GET",
      headers,
    });

    const json = (await response.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
    if (!response.ok) {
      throw new Error(json?.error || "Progress özeti alınamadı");
    }

    return json;
  } catch (error) {
    const msg = error instanceof Error ? error.message.toLowerCase() : "";
    if (msg.includes("failed to fetch") || msg.includes("network") || msg.includes("abort")) {
      return null;
    }
    throw error;
  }
}

export async function fetchProgressTimeline() {
  const headers = await getProgressAuthHeaders();
  if (!headers) return null;

  try {
    const response = await resilientFetch("/api/numeroloji/progress/timeline", {
      method: "GET",
      headers,
    });

    const json = (await response.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
    if (!response.ok) {
      throw new Error(json?.error || "Progress zaman akışı alınamadı");
    }

    return json;
  } catch (error) {
    const msg = error instanceof Error ? error.message.toLowerCase() : "";
    if (msg.includes("failed to fetch") || msg.includes("network") || msg.includes("abort")) {
      return null;
    }
    throw error;
  }
}

export async function fetchNotifications(limit = 50) {
  const headers = await getProgressAuthHeaders();
  if (!headers) return { items: [] as NotificationInboxItem[], unreadCount: 0 };

  const response = await resilientFetch(`/api/numeroloji/notifications?limit=${Math.max(1, Math.min(100, Math.trunc(limit)))}`, {
    method: "GET",
    headers,
  });

  const json = (await response.json().catch(() => null)) as { ok?: boolean; error?: string; items?: NotificationInboxItem[]; unreadCount?: number } | null;
  if (!response.ok || !json?.ok) {
    throw new Error(json?.error || "Bildirimler alınamadı");
  }

  return {
    items: Array.isArray(json.items) ? json.items : [],
    unreadCount: Math.max(0, Number(json.unreadCount ?? 0)),
  };
}

export async function markNotificationsRead(input: { ids?: string[]; all?: boolean }) {
  const headers = await getProgressAuthHeaders();
  if (!headers) return null;

  const response = await resilientFetch("/api/numeroloji/notifications/read", {
    method: "POST",
    headers,
    body: JSON.stringify({ ids: input.ids ?? [], all: Boolean(input.all) }),
  });

  const json = (await response.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
  if (!response.ok || !json?.ok) {
    throw new Error(json?.error || "Bildirim güncellenemedi");
  }

  return json;
}

export async function markNotificationClicked(notificationId: string) {
  const headers = await getProgressAuthHeaders();
  if (!headers) return null;

  const response = await resilientFetch("/api/numeroloji/notifications/click", {
    method: "POST",
    headers,
    body: JSON.stringify({ notificationId }),
  });

  const json = (await response.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
  if (!response.ok || !json?.ok) {
    throw new Error(json?.error || "Bildirim tıklama durumu güncellenemedi");
  }

  return json;
}

export async function deleteNotifications(input: { ids?: string[]; all?: boolean }) {
  const headers = await getProgressAuthHeaders();
  if (!headers) return null;

  const response = await resilientFetch("/api/numeroloji/notifications/delete", {
    method: "POST",
    headers,
    body: JSON.stringify({ ids: input.ids ?? [], all: Boolean(input.all) }),
  });

  const json = (await response.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
  if (!response.ok || !json?.ok) {
    throw new Error(json?.error || "Bildirimler silinemedi");
  }

  return json;
}

export async function fetchNotificationPreferences() {
  const headers = await getProgressAuthHeaders();
  if (!headers) return null;

  const response = await resilientFetch("/api/numeroloji/notifications/preferences", {
    method: "GET",
    headers,
  });

  const json = (await response.json().catch(() => null)) as { ok?: boolean; error?: string; item?: NotificationPreferences } | null;
  if (!response.ok || !json?.ok) {
    throw new Error(json?.error || "Bildirim tercihleri alınamadı");
  }

  return json.item ?? null;
}

export async function updateNotificationPreferences(input: Partial<NotificationPreferences>) {
  const headers = await getProgressAuthHeaders();
  if (!headers) return null;

  const response = await resilientFetch("/api/numeroloji/notifications/preferences", {
    method: "POST",
    headers,
    body: JSON.stringify(input),
  });

  const json = (await response.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
  if (!response.ok || !json?.ok) {
    throw new Error(json?.error || "Bildirim tercihleri kaydedilemedi");
  }

  return json;
}

export async function registerNotificationDevice(input: {
  platform?: "web" | "ios" | "android";
  push_provider?: "webpush" | "fcm" | "apns";
  device_token: string;
  endpoint?: string;
  p256dh?: string;
  auth_secret?: string;
  app_version?: string;
  locale?: string;
}) {
  const headers = await getProgressAuthHeaders();
  if (!headers) return null;

  const response = await resilientFetch("/api/numeroloji/notifications/devices/register", {
    method: "POST",
    headers,
    body: JSON.stringify(input),
  });

  const json = (await response.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
  if (!response.ok || !json?.ok) {
    throw new Error(json?.error || "Cihaz bildirimi kaydedilemedi");
  }

  return json;
}

export async function deactivateNotificationDevice(deviceToken: string) {
  const headers = await getProgressAuthHeaders();
  if (!headers) return null;

  const response = await resilientFetch("/api/numeroloji/notifications/devices/deactivate", {
    method: "POST",
    headers,
    body: JSON.stringify({ device_token: deviceToken }),
  });

  const json = (await response.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
  if (!response.ok || !json?.ok) {
    throw new Error(json?.error || "Cihaz bildirimi pasifleştirilemedi");
  }

  return json;
}

export async function processNotificationDispatchQueue() {
  const headers = await getProgressAuthHeaders();
  if (!headers) return null;

  const response = await resilientFetch("/api/numeroloji/notifications/dispatch/process", {
    method: "POST",
    headers,
    body: JSON.stringify({}),
  });

  const json = (await response.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
  if (!response.ok || !json?.ok) {
    throw new Error(json?.error || "Bildirim kuyruğu işlenemedi");
  }

  return json;
}

function toBase64Url(uint8: ArrayBuffer | Uint8Array) {
  const bytes = uint8 instanceof Uint8Array ? uint8 : new Uint8Array(uint8);
  let str = "";
  for (const byte of bytes) str += String.fromCharCode(byte);
  return globalThis.btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function fromBase64Url(base64Url: string) {
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = globalThis.atob(padded);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export async function enableBrowserPushNotifications() {
  if (typeof window === "undefined") {
    return { ok: false, reason: "server" as const };
  }

  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return { ok: false, reason: "unsupported" as const };
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return { ok: false, reason: "permission_denied" as const };
  }

  const vapidPublicKey = readRuntimeEnv("VITE_WEB_PUSH_VAPID_PUBLIC_KEY");
  if (!vapidPublicKey) {
    return { ok: false, reason: "missing_vapid_key" as const };
  }

  const registration = await navigator.serviceWorker.register("/notification-sw.js");
  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: fromBase64Url(vapidPublicKey),
    }));

  const json = subscription.toJSON();
  const endpoint = String(json.endpoint ?? "").trim();
  const p256dh = json.keys?.p256dh ? String(json.keys.p256dh) : "";
  const authSecret = json.keys?.auth ? String(json.keys.auth) : "";
  if (!endpoint) {
    return { ok: false, reason: "subscription_failed" as const };
  }

  await registerNotificationDevice({
    platform: "web",
    push_provider: "webpush",
    device_token: endpoint,
    endpoint,
    p256dh,
    auth_secret: authSecret,
    app_version: readRuntimeEnv("VITE_APP_VERSION") || undefined,
    locale: typeof navigator.language === "string" ? navigator.language : undefined,
  });

  return {
    ok: true,
    subscription: {
      endpoint,
      p256dh,
      authSecret,
      raw: json,
    },
  };
}

export async function disableBrowserPushNotifications() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

  const registration = await navigator.serviceWorker.ready.catch(() => null);
  const subscription = await registration?.pushManager.getSubscription().catch(() => null);

  const endpoint = String(subscription?.endpoint ?? "").trim();
  if (endpoint) {
    await deactivateNotificationDevice(endpoint).catch(() => null);
  }

  await subscription?.unsubscribe().catch(() => null);
}

export async function getCurrentBrowserPushToken() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return null;
  const registration = await navigator.serviceWorker.ready.catch(() => null);
  const subscription = await registration?.pushManager.getSubscription().catch(() => null);
  if (!subscription) return null;

  const json = subscription.toJSON();
  const endpoint = String(json.endpoint ?? "").trim();
  if (!endpoint) return null;

  return {
    endpoint,
    p256dh: json.keys?.p256dh ? String(json.keys.p256dh) : "",
    authSecret: json.keys?.auth ? String(json.keys.auth) : "",
    tokenHash: toBase64Url(new TextEncoder().encode(endpoint)),
  };
}
